import { useState } from 'react';
import { createTenantMatchRequest } from '../../../lib/supabase';
import { supabase } from '../../../lib/supabase';
import { useTokens } from '../../../hooks/useTokens';
import TokenCountdown from '../../../components/feature/TokenCountdown';

interface AiMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const REGIONS = ['全部地區', '台北市', '新北市', '桃園市', '台中市', '台南市', '高雄市'];
const ROOM_OPTIONS = ['1房', '2房', '3房', '4房以上'];

export default function AiMatchModal({ isOpen, onClose }: AiMatchModalProps) {
  const [region, setRegion] = useState('');
  const [maxPrice, setMaxPrice] = useState(30000);
  const [minArea, setMinArea] = useState<number | ''>('');
  const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
  const [petAllowed, setPetAllowed] = useState(false);
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [lineId, setLineId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const { balance, spendToken } = useTokens();

  const handleRoomToggle = (room: string) => {
    setSelectedRooms(prev =>
      prev.includes(room) ? prev.filter(r => r !== room) : [...prev, room]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTokenError('');
    if (!region) return;
    if (selectedRooms.length === 0) return;
    if (!contactName.trim() || !contactPhone.trim() || !contactEmail.trim()) return;

    if (balance === null || balance < 1) {
      setTokenError('通證不足！使用一次 AI 媒合需要 1 張通證。每日登入可獲得 1 張，首次加入獲得 10 張。');
      return;
    }

    setIsSubmitting(true);
    try {
      const tokenResult = await spendToken('🤖 使用 AI 媒合找房');
      if (!tokenResult.success) {
        setTokenError(tokenResult.error || '通證扣除失敗，請稍後再試');
        setIsSubmitting(false);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      await createTenantMatchRequest({
        region,
        max_price: maxPrice,
        min_area: minArea === '' ? undefined : Number(minArea),
        rooms: selectedRooms,
        pet_allowed: petAllowed,
        contact_name: contactName.trim(),
        contact_phone: contactPhone.trim(),
        contact_email: contactEmail.trim(),
        line_id: lineId.trim() || undefined,
        user_id: user?.id,
        session_token: user ? undefined : crypto.randomUUID(),
      });

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onClose();
        setRegion(''); setMaxPrice(30000); setMinArea(''); setSelectedRooms([]);
        setPetAllowed(false); setContactName(''); setContactPhone('');
        setContactEmail(''); setLineId('');
      }, 2200);
    } catch (error) {
      console.error('建立匹配請求失敗:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4 bg-black/75 backdrop-blur-sm">

      {/* 科技動畫 keyframes */}
      <style>{`
        @keyframes modalScanLine {
          0%   { transform: translateY(-60px); opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { transform: translateY(100%); opacity: 0; }
        }
        @keyframes techBorderPulse {
          0%, 100% { opacity: 0.4; }
          50%       { opacity: 0.9; }
        }
        @keyframes successRing {
          0%   { transform: scale(0.6); opacity: 0; }
          60%  { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1);   opacity: 1; }
        }
        .modal-scan { animation: modalScanLine 3.5s ease-in-out infinite; }
        .tech-border-pulse { animation: techBorderPulse 2.5s ease-in-out infinite; }
        .success-ring { animation: successRing 0.5s cubic-bezier(.175,.885,.32,1.275) forwards; }
      `}</style>

      <div className="relative w-full sm:max-w-lg max-h-[92vh] sm:max-h-[88vh] flex flex-col rounded-t-3xl sm:rounded-3xl overflow-hidden"
        style={{ background: 'rgba(8,14,30,0.97)', border: '1px solid rgba(45,212,191,0.3)' }}>

        {/* 背景科技網格 */}
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'linear-gradient(rgba(45,212,191,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(45,212,191,0.025) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }} />

        {/* 背景光暈 */}
        <div className="absolute -top-20 -left-20 w-64 h-64 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(45,212,191,0.08) 0%, transparent 70%)' }} />
        <div className="absolute -bottom-10 -right-10 w-48 h-48 rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(20,184,166,0.06) 0%, transparent 70%)' }} />

        {/* 掃描光線 */}
        <div className="modal-scan absolute left-0 right-0 pointer-events-none z-0"
          style={{ height: '60px', background: 'linear-gradient(to bottom, transparent, rgba(45,212,191,0.04), transparent)' }} />

        {/* 角落裝飾 */}
        {['top-0 left-0 border-t border-l rounded-tl-3xl', 'top-0 right-0 border-t border-r rounded-tr-3xl'].map((cls, i) => (
          <div key={i} className={`absolute ${cls} w-8 h-8 pointer-events-none tech-border-pulse`}
            style={{ borderColor: 'rgba(45,212,191,0.6)' }} />
        ))}

        {/* ── Header ── */}
        <div className="relative z-10 flex-shrink-0 px-5 py-4 border-b"
          style={{ borderColor: 'rgba(45,212,191,0.2)', background: 'rgba(8,14,30,0.95)' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* 中央旋轉核心 */}
              <div className="relative w-10 h-10 flex items-center justify-center flex-shrink-0">
                <div className="absolute inset-0 rounded-full border border-dashed border-teal-400/30 animate-spin" style={{ animationDuration: '6s' }} />
                <div className="absolute inset-1 rounded-full border border-teal-400/20 animate-spin" style={{ animationDuration: '4s', animationDirection: 'reverse' }} />
                <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: 'rgba(45,212,191,0.15)', border: '1px solid rgba(45,212,191,0.4)' }}>
                  <i className="ri-robot-2-fill text-teal-400 text-base"></i>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-black text-white">AI 自動匹配</h2>
                  <div className="flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: 'rgba(45,212,191,0.15)', border: '1px solid rgba(45,212,191,0.3)' }}>
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-teal-400"></span>
                    </span>
                    <span className="text-teal-400 text-[9px] font-mono">ONLINE</span>
                  </div>
                </div>
                <p className="text-[10px] font-mono text-teal-400/60 mt-0.5 tracking-wider">AI_MATCH_ENGINE · v2.1</p>
              </div>
            </div>
            <button onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <i className="ri-close-line text-gray-400 text-lg"></i>
            </button>
          </div>

          {/* 通證狀態列 */}
          {balance !== null && (
            <div className={`mt-3 flex items-center gap-2 px-3 py-2 rounded-xl text-xs ${
              balance === 0
                ? 'bg-red-500/10 border border-red-500/30 text-red-300'
                : 'bg-amber-400/8 border border-amber-400/25 text-amber-300'
            }`}>
              <i className={`ri-copper-coin-fill text-sm ${balance === 0 ? 'text-red-400' : 'text-amber-400'}`}></i>
              <span className="font-mono">
                使用 AI 媒合消耗 1 張通證
                {balance > 0 && <span className="ml-1 font-bold text-white">· 剩 {balance} 張</span>}
                {balance === 0 && <span className="ml-1 text-red-400">· 不足，每日登入可獲得</span>}
              </span>
            </div>
          )}
        </div>

        {/* ── 成功畫面 ── */}
        {showSuccess && (
          <div className="absolute inset-0 z-30 flex items-center justify-center rounded-3xl"
            style={{ background: 'rgba(8,14,30,0.97)' }}>
            <div className="text-center px-6">
              <div className="relative w-24 h-24 mx-auto mb-5">
                <div className="absolute inset-0 rounded-full animate-ping" style={{ background: 'rgba(45,212,191,0.12)', animationDuration: '1.5s' }} />
                <div className="success-ring absolute inset-3 rounded-full flex items-center justify-center border-4 border-teal-400">
                  <i className="ri-check-double-line text-3xl text-teal-400"></i>
                </div>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-3"
                style={{ background: 'rgba(45,212,191,0.15)', border: '1px solid rgba(45,212,191,0.3)' }}>
                <i className="ri-sparkling-2-fill text-teal-400 text-xs"></i>
                <span className="text-teal-400 text-xs font-mono">MATCHING_TASK_LAUNCHED</span>
              </div>
              <h3 className="text-xl font-black text-white mb-2">設定成功！</h3>
              <p className="text-sm text-gray-400">有符合條件的房源會立即通知您</p>
            </div>
          </div>
        )}

        {/* ── 表單主體（可捲動）── */}
        <form id="ai-match-form" onSubmit={handleSubmit}
          className="relative z-10 flex-1 overflow-y-auto px-5 py-4 space-y-4"
          style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}>

          {/* 通證不足錯誤 */}
          {tokenError && (
            <div className="space-y-2">
              <div className="flex items-start gap-2 px-3 py-2.5 rounded-xl text-xs"
                style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
                <i className="ri-error-warning-fill text-red-400 text-sm flex-shrink-0 mt-0.5"></i>
                <p className="text-red-300 leading-relaxed">{tokenError}</p>
              </div>
              <TokenCountdown card />
            </div>
          )}

          {/* 地區 */}
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-teal-400 mb-2">
              <i className="ri-map-pin-line text-teal-400 text-sm"></i>
              租屋地區 <span className="text-red-400 ml-0.5">*</span>
            </label>
            <div className="relative">
              <select value={region} onChange={e => setRegion(e.target.value)}
                className="w-full appearance-none px-4 py-3 rounded-xl text-sm font-medium outline-none transition-all cursor-pointer text-white"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(45,212,191,0.25)', color: region ? 'white' : 'rgba(156,163,175,1)' }}
                required>
                <option value="" style={{ background: '#0f172a' }}>請選擇地區</option>
                {REGIONS.map(r => <option key={r} value={r} style={{ background: '#0f172a' }}>{r}</option>)}
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-teal-400/60 pointer-events-none"></i>
            </div>
          </div>

          {/* 租金上限 */}
          <div>
            <label className="flex items-center justify-between mb-2">
              <span className="flex items-center gap-2 text-xs font-bold text-teal-400">
                <i className="ri-money-dollar-circle-line text-teal-400 text-sm"></i>
                月租金上限 <span className="text-red-400 ml-0.5">*</span>
              </span>
              <span className="px-3 py-1 rounded-lg text-sm font-black text-white"
                style={{ background: 'rgba(45,212,191,0.2)', border: '1px solid rgba(45,212,191,0.35)' }}>
                NT$ {maxPrice.toLocaleString()}
              </span>
            </label>
            <input type="range" min="5000" max="100000" step="1000" value={maxPrice}
              onChange={e => setMaxPrice(Number(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-teal-500"
              style={{ background: 'rgba(45,212,191,0.15)' }} />
            <div className="flex justify-between mt-1.5">
              <span className="text-[10px] font-mono text-gray-500">NT$5k</span>
              <span className="text-[10px] font-mono text-gray-500">NT$100k</span>
            </div>
          </div>

          {/* 房型 */}
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-teal-400 mb-2">
              <i className="ri-door-line text-teal-400 text-sm"></i>
              房型需求 <span className="text-red-400 ml-0.5">*</span>
              <span className="text-gray-500 font-normal text-[10px]">可多選</span>
            </label>
            <div className="grid grid-cols-4 gap-2">
              {ROOM_OPTIONS.map(room => (
                <button key={room} type="button" onClick={() => handleRoomToggle(room)}
                  className="py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer whitespace-nowrap"
                  style={selectedRooms.includes(room) ? {
                    background: 'rgba(45,212,191,0.25)',
                    border: '1.5px solid rgba(45,212,191,0.7)',
                    color: '#2dd4bf',
                    boxShadow: '0 0 10px rgba(45,212,191,0.2)',
                  } : {
                    background: 'rgba(255,255,255,0.05)',
                    border: '1.5px solid rgba(255,255,255,0.1)',
                    color: 'rgba(156,163,175,1)',
                  }}>
                  {selectedRooms.includes(room) && <i className="ri-check-line mr-1 text-teal-400"></i>}
                  {room}
                </button>
              ))}
            </div>
          </div>

          {/* 坪數 */}
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-teal-400 mb-2">
              <i className="ri-ruler-line text-teal-400 text-sm"></i>
              最低坪數
              <span className="text-gray-500 font-normal text-[10px]">選填</span>
            </label>
            <div className="relative">
              <input type="number" min="0" step="0.1" value={minArea}
                onChange={e => setMinArea(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="不限制"
                className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all text-white placeholder:text-gray-600"
                style={{ background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(45,212,191,0.2)' }} />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-mono text-teal-400/60">坪</span>
            </div>
          </div>

          {/* 寵物 */}
          <label className="flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer transition-all"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1.5px solid rgba(45,212,191,0.15)' }}>
            <input type="checkbox" checked={petAllowed} onChange={e => setPetAllowed(e.target.checked)}
              className="w-4 h-4 accent-teal-500 cursor-pointer rounded" />
            <i className="ri-bear-smile-line text-lg text-teal-400"></i>
            <div>
              <p className="text-sm font-bold text-white">寵物友善</p>
              <p className="text-[10px] text-gray-500">只匹配允許飼養寵物的房源</p>
            </div>
          </label>

          {/* 聯絡資訊 */}
          <div>
            <div className="flex items-center gap-2 mb-3 pb-2" style={{ borderBottom: '1px solid rgba(45,212,191,0.15)' }}>
              <i className="ri-contacts-line text-teal-400 text-sm"></i>
              <span className="text-xs font-bold text-teal-400">聯絡資訊</span>
            </div>
            <div className="space-y-3">
              {[
                { label: '姓名', placeholder: '請輸入您的姓名', value: contactName, onChange: (v: string) => setContactName(v), type: 'text', required: true },
                { label: '電話', placeholder: '請輸入您的電話', value: contactPhone, onChange: (v: string) => setContactPhone(v), type: 'tel', required: true },
                { label: 'Email', placeholder: '請輸入您的 Email', value: contactEmail, onChange: (v: string) => setContactEmail(v), type: 'email', required: true },
              ].map(field => (
                <div key={field.label}>
                  <label className="block text-xs font-semibold text-gray-400 mb-1.5">
                    {field.label} {field.required && <span className="text-red-400">*</span>}
                  </label>
                  <input type={field.type} value={field.value}
                    onChange={e => field.onChange(e.target.value)}
                    placeholder={field.placeholder}
                    className="w-full px-4 py-2.5 rounded-xl text-sm outline-none transition-all text-white placeholder:text-gray-600"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(45,212,191,0.2)' }}
                    required={field.required} />
                </div>
              ))}

              {/* LINE */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-400 mb-1.5">
                  <span className="inline-flex w-3.5 h-3.5 items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#06C755' }}>
                    <i className="ri-line-fill text-white" style={{ fontSize: '8px' }}></i>
                  </span>
                  LINE ID <span className="text-gray-600 font-normal text-[10px] ml-1">選填</span>
                </label>
                <input type="text" value={lineId} onChange={e => setLineId(e.target.value)}
                  placeholder="@yourlineid（配對成功時 LINE 通知）"
                  className="w-full px-4 py-2.5 rounded-xl text-sm outline-none transition-all text-white placeholder:text-gray-600"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(6,199,85,0.25)' }} />
              </div>

              {/* LINE OA 加入提示 */}
              <a href="https://line.me/R/ti/p/@381szuhf" target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-between w-full px-4 py-3 rounded-xl transition-opacity hover:opacity-90 cursor-pointer"
                style={{ backgroundColor: '#06C755' }}>
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                    <i className="ri-line-fill text-white text-base"></i>
                  </div>
                  <div>
                    <p className="text-xs font-black text-white leading-none">加入 LINE 官方帳號</p>
                    <p className="text-[9px] text-white/70 mt-0.5">配對成功即時推送通知</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 bg-white/20 rounded-full px-2 py-1 flex-shrink-0">
                  <i className="ri-flashlight-fill text-white text-[10px]"></i>
                  <span className="text-[10px] text-white font-bold">立即加入</span>
                </div>
              </a>
            </div>
          </div>

          {/* 說明 */}
          <div className="rounded-xl px-4 py-3 text-xs"
            style={{ background: 'rgba(45,212,191,0.06)', border: '1px solid rgba(45,212,191,0.2)' }}>
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-robot-2-line text-teal-400 text-sm flex-shrink-0"></i>
              <span className="text-teal-400 font-bold text-xs">媒合說明</span>
            </div>
            <ul className="space-y-1 text-gray-400">
              {[
                '系統自動比對符合你條件的新房源',
                '配對成功即時發送 LINE + Email 雙通知',
                '可隨時在個人帳戶查看或修改條件',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-teal-400/60 font-mono flex-shrink-0">{String(i + 1).padStart(2, '0')}.</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* 底部間距 */}
          <div className="h-2" />
        </form>

        {/* ── 底部操作按鈕 ── */}
        <div className="relative z-10 flex-shrink-0 px-5 py-4 flex gap-3"
          style={{ borderTop: '1px solid rgba(45,212,191,0.15)', background: 'rgba(8,14,30,0.98)' }}>
          <button type="button" onClick={onClose}
            className="flex-1 py-3 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap text-gray-400"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
            取消
          </button>
          <button type="submit" form="ai-match-form" onClick={handleSubmit} disabled={isSubmitting}
            className="flex-1 py-3 rounded-xl text-sm font-black transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50 text-white"
            style={{
              background: isSubmitting ? 'rgba(45,212,191,0.4)' : 'rgba(45,212,191,1)',
              border: '1px solid rgba(45,212,191,0.6)',
              boxShadow: '0 0 20px rgba(45,212,191,0.25)',
            }}>
            {isSubmitting ? (
              <><i className="ri-loader-4-line animate-spin"></i>處理中...</>
            ) : (
              <><i className="ri-sparkling-2-fill"></i>開始 AI 媒合</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
